// src/components/NotificationToast/NotificationToast.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import InventoryIcon from '@mui/icons-material/Inventory';
import VerifiedIcon from '@mui/icons-material/Verified';
import { NotificationItem } from '../../services/notificationAPI';
import { ToastNotification } from '../../hooks/useNotificationToasts';
import styles from './NotificationToast.module.css';

interface NotificationToastProps {
  notification: ToastNotification;
  onClose: (toastId: string) => void;
  onClick: (notification: NotificationItem) => void;
}

export const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onClose,
  onClick
}) => {
  const [isExiting, setIsExiting] = useState(false);

  // 1. Memorizar a função handleClose
  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(notification.toastId);
    }, 300);
  }, [onClose, notification.toastId]); // Dependências necessárias

  useEffect(() => {
    // Auto-dismiss após 5 segundos
    const timer = setTimeout(() => {
      handleClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [handleClose]); // 2. Agora é seguro adicionar handleClose aqui

  const getIcon = () => {
    switch (notification.type) {
      case 'alert':
        return <WarningAmberIcon sx={{ fontSize: 20 }} />;
      case 'qc_autorizado':
        return <VerifiedIcon sx={{ fontSize: 20 }} />;
      case 'early_success':
        return <AccessTimeIcon sx={{ fontSize: 20 }} />;
      case 'success':
        return <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />;
      case 'sale':
        return <TrendingUpIcon sx={{ fontSize: 20 }} />;
      case 'goal':
        return <TrackChangesIcon sx={{ fontSize: 20 }} />;
      case 'client':
        return <PersonAddIcon sx={{ fontSize: 20 }} />;
      case 'product':
        return <InventoryIcon sx={{ fontSize: 20 }} />;
      default:
        return <NotificationsIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getIconColor = () => {
    switch (notification.type) {
      case 'alert':
        return '#ef4444';
      case 'qc_autorizado':
        return '#d97706';
      case 'early_success':
        return '#16a34a';
      case 'success':
        return '#16a34a';
      case 'sale':
        return '#3b82f6';
      case 'goal':
        return '#8b5cf6';
      case 'client':
        return '#ec4899';
      case 'product':
        return '#f59e0b';
      default:
        return '#64748b';
    }
  };

  const getBackgroundColor = () => {
    switch (notification.type) {
      case 'alert':
        return '#fef2f2';
      case 'qc_autorizado':
        return '#fffbeb';
      case 'early_success':
        return '#f0fdf4';
      case 'success':
        return '#f0fdf4';
      case 'sale':
        return '#eff6ff';
      case 'goal':
        return '#f5f3ff';
      case 'client':
        return '#fdf2f8';
      case 'product':
        return '#fffbeb';
      default:
        return '#ffffff';
    }
  };

  const getBorderColor = () => {
    switch (notification.type) {
      case 'alert':
        return '#ef4444';
      case 'qc_autorizado':
        return '#d97706';
      case 'early_success':
        return '#16a34a';
      case 'success':
        return '#16a34a';
      case 'sale':
        return '#3b82f6';
      case 'goal':
        return '#8b5cf6';
      case 'client':
        return '#ec4899';
      case 'product':
        return '#f59e0b';
      default:
        return '#cbd5e1';
    }
  };

  const getIconBgColor = () => {
    switch (notification.type) {
      case 'alert':
        return 'rgba(239, 68, 68, 0.08)';
      case 'qc_autorizado':
        return 'rgba(217, 119, 6, 0.08)';
      case 'early_success':
        return 'rgba(22, 163, 74, 0.08)';
      case 'success':
        return 'rgba(22, 163, 74, 0.08)';
      case 'sale':
        return 'rgba(59, 130, 246, 0.08)';
      case 'goal':
        return 'rgba(139, 92, 246, 0.08)';
      case 'client':
        return 'rgba(236, 72, 153, 0.08)';
      case 'product':
        return 'rgba(245, 158, 11, 0.08)';
      default:
        return 'rgba(100, 116, 139, 0.08)';
    }
  };

  return (
    <div
      className={`${styles.toast} ${isExiting ? styles.exiting : ''}`}
      onClick={() => onClick(notification)}
    >
      <button
        className={styles.closeButton}
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </button>

      <div className={styles.toastContent}>
        <div
          className={styles.iconWrapper}
          style={{ 
            color: getIconColor(),
            backgroundColor: getIconBgColor()
          }}
        >
          {getIcon()}
        </div>

        <div className={styles.textContent}>
          <h4 className={styles.title}>{notification.title}</h4>
          <p className={styles.description}>{notification.description}</p>
          <span className={styles.time}>{notification.time}</span>
        </div>
      </div>
    </div>
  );
};

interface NotificationToastContainerProps {
  toasts: ToastNotification[];
  onClose: (toastId: string) => void;
  onClick: (notification: NotificationItem) => void;
}

export const NotificationToastContainer: React.FC<NotificationToastContainerProps> = ({
  toasts,
  onClose,
  onClick
}) => {
  if (toasts.length === 0) return null;

  return createPortal(
    <div className={styles.toastContainer}>
      {toasts.map((toast) => (
        <NotificationToast
          key={toast.toastId}
          notification={toast}
          onClose={onClose}
          onClick={onClick}
        />
      ))}
    </div>,
    document.body
  );
};